import {RUINS_INCOME_GLOW} from '../../configs/ruinsIncomeGlow';

/**
 * Расчеты свечения руин в раунды с увеличенным доходом (см.
 * configs/ruinsIncomeGlow): дыхание ореола и иконки, волна света по иконке,
 * вспышки звездочек и картинки, которые свечение рисует себе само. Здесь только
 * числа — спрайтами владеет scenes/RuinsIncomeGlow. Размеры и места звездочек
 * заданы в долях жетона, поэтому от его места и размера на поле не зависят
 */

const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

const resolveSpread = (rng, value, spread) => value * (1 - spread + 2 * spread * rng());

/**
 * Насколько свечение разгорелось после появления: от 0 до 1
 * @param {number} elapsedMs — сколько свечение уже горит
 * @param {number} [fadeInMs]
 * @returns {number}
 */
export const resolveGlowFadeIn = (elapsedMs, fadeInMs = RUINS_INCOME_GLOW.fadeInMs) => (
  fadeInMs > 0 ? clamp01(elapsedMs / fadeInMs) : 1
);

/**
 * Где дыхание: 0 — выдох (слабее всего), 1 — вдох. Плавно, по косинусу
 * @param {number} elapsedMs — время от начала дыхания
 * @param {number} [periodMs]
 * @returns {number}
 */
export const resolveBreath = (elapsedMs, periodMs = RUINS_INCOME_GLOW.breathPeriodMs) => (
  0.5 - 0.5 * Math.cos((2 * Math.PI * elapsedMs) / periodMs)
);

/**
 * Яркость в данный момент дыхания
 * @param {{alphaMin: number, alphaMax: number}} range
 * @param {number} breath — от 0 до 1 (resolveBreath)
 * @returns {number}
 */
export const resolveBreathAlpha = ({alphaMin, alphaMax}, breath) => alphaMin + (alphaMax - alphaMin) * breath;

/**
 * Сколько ждать до следующей волны по иконке: средний промежуток с разбросом
 * @param {function(): number} rng — источник случайности, от 0 до 1
 * @param {Object} [shimmer]
 * @returns {number} мс
 */
export const resolveShimmerPeriodMs = (rng, shimmer = RUINS_INCOME_GLOW.shimmer) => (
  // Волна обязана успеть пройти до следующей
  Math.max(resolveSpread(rng, shimmer.periodMs, shimmer.periodSpread), shimmer.durationMs)
);

/**
 * Какой кадр волны сейчас на иконке
 * @param {number} cycleElapsedMs — время от начала текущей волны
 * @param {Object} [shimmer]
 * @returns {number|null} номер кадра; null — волна прошла, ждем следующую
 */
export const resolveShimmerFrame = (cycleElapsedMs, shimmer = RUINS_INCOME_GLOW.shimmer) => {
  if (!(cycleElapsedMs >= 0) || cycleElapsedMs >= shimmer.durationMs) {
    return null;
  }
  return Math.min(shimmer.frameCount - 1, Math.floor((cycleElapsedMs / shimmer.durationMs) * shimmer.frameCount));
};

/**
 * Полоса света в кадре волны — доли диагонали иконки от нижнего левого угла
 * (0) к верхнему правому (1). В первом кадре полоса еще целиком за углом, в
 * последнем — уже за противоположным: волна не возникает и не обрывается
 * посреди иконки
 * @param {number} frameIndex
 * @param {Object} [shimmer]
 * @returns {{start: number, peak: number, end: number}}
 */
export const resolveShimmerBand = (frameIndex, shimmer = RUINS_INCOME_GLOW.shimmer) => {
  const halfBand = shimmer.bandShare / 2;
  const progress = shimmer.frameCount > 1 ? frameIndex / (shimmer.frameCount - 1) : 0.5;
  const peak = -halfBand + (1 + shimmer.bandShare) * progress;
  return {start: peak - halfBand, peak, end: peak + halfBand};
};

/**
 * Опоры градиента кадра волны на диагонали иконки: [доля диагонали, яркость].
 * Свет в полосе нарастает к ее середине и спадает к краям; опоры стоят на
 * концах диагонали и на изломах полосы, поэтому градиент повторяет полосу
 * точно, даже когда она заходит за угол иконки
 * @param {number} frameIndex
 * @param {Object} [shimmer]
 * @returns {Array<[number, number]>} по возрастанию доли
 */
export const resolveShimmerGradientStops = (frameIndex, shimmer = RUINS_INCOME_GLOW.shimmer) => {
  const {start, peak, end} = resolveShimmerBand(frameIndex, shimmer);
  const halfBand = peak - start;
  const resolveAlpha = (offset) => (halfBand > 0 ? clamp01(1 - Math.abs(offset - peak) / halfBand) : 0);

  return [...new Set([0, start, peak, end, 1].filter((offset) => offset >= 0 && offset <= 1))]
    .sort((a, b) => a - b)
    .map((offset) => [offset, resolveAlpha(offset)]);
};

/**
 * Насколько пиксель картинки руин — иконка: темная иконка на светлом камне
 * @param {number} luma — яркость пикселя, 0–255
 * @param {number} alpha — непрозрачность пикселя, 0–255
 * @param {Object} [icon]
 * @returns {number} от 0 (камень) до 1 (иконка)
 */
export const resolveIconGlyphAlpha = (luma, alpha, icon = RUINS_INCOME_GLOW.icon) => (
  clamp01((icon.lumaLight - luma) / (icon.lumaLight - icon.lumaDark)) * clamp01(alpha / 255)
);

/**
 * Размеры картинок силуэта иконки: сам силуэт (glyph) и клетка кадра (cell) —
 * силуэт с запасом под ореол волны со всех сторон
 * @param {Object} [config]
 * @returns {{glyphWidth: number, glyphHeight: number, padding: number, cellWidth: number, cellHeight: number}}
 */
export const resolveIconTextureLayout = ({icon, shimmer} = RUINS_INCOME_GLOW) => {
  const glyphWidth = Math.max(1, Math.round(icon.sourceRect.width * icon.textureScale));
  const glyphHeight = Math.max(1, Math.round(icon.sourceRect.height * icon.textureScale));
  const padding = Math.ceil(shimmer.glowBlur * 2);
  return {
    glyphWidth,
    glyphHeight,
    padding,
    cellWidth: glyphWidth + 2 * padding,
    cellHeight: glyphHeight + 2 * padding,
  };
};

/**
 * Где на поле силуэт иконки и в каком масштабе: ровно поверх иконки,
 * нарисованной на жетоне. Спрайт жетона показывает картинку целиком с одним
 * масштабом — ширина жетона на поле к ширине картинки
 * @param {{x: number, y: number, width: number}} token — центр и ширина жетона на поле
 * @param {{width: number, height: number}} image — размер картинки жетона
 * @param {Object} [config]
 * @returns {{x: number, y: number, scaleX: number, scaleY: number}}
 */
export const resolveIconPlacement = ({x, y, width}, image, config = RUINS_INCOME_GLOW) => {
  const {sourceRect} = config.icon;
  const {glyphWidth, glyphHeight} = resolveIconTextureLayout(config);
  const tokenScale = width / image.width;

  return {
    x: x + (sourceRect.x + sourceRect.width / 2 - image.width / 2) * tokenScale,
    y: y + (sourceRect.y + sourceRect.height / 2 - image.height / 2) * tokenScale,
    // Силуэт хранится уменьшенным (textureScale), а размер округлен до пикселя
    scaleX: (tokenScale * sourceRect.width) / glyphWidth,
    scaleY: (tokenScale * sourceRect.height) / glyphHeight,
  };
};

/**
 * Картинка ореола: размер холста и контур шестиугольника на нем (вершины
 * слева и справа, как у жетона), утопленный от края на insetShare. Вокруг
 * контура запас под размытие света
 * @param {Object} [texture]
 * @returns {{width: number, height: number, hexWidth: number, hexHeight: number, lineWidth: number, blur: number, points: Array<{x: number, y: number}>}}
 */
export const resolveRimTextureLayout = (texture = RUINS_INCOME_GLOW.rim.texture) => {
  const hexWidth = texture.hexWidth;
  const hexHeight = hexWidth * texture.heightShare;
  const lineWidth = hexWidth * texture.lineShare;
  const blur = hexWidth * texture.blurShare;
  const padding = Math.ceil(blur * 2 + lineWidth);
  const centerX = padding + hexWidth / 2;
  const centerY = padding + hexHeight / 2;
  const halfWidth = (hexWidth / 2) * (1 - texture.insetShare);
  const halfHeight = (hexHeight / 2) * (1 - texture.insetShare);

  return {
    width: Math.ceil(hexWidth + 2 * padding),
    height: Math.ceil(hexHeight + 2 * padding),
    hexWidth,
    hexHeight,
    lineWidth,
    blur,
    points: [
      {x: centerX - halfWidth, y: centerY},
      {x: centerX - halfWidth / 2, y: centerY - halfHeight},
      {x: centerX + halfWidth / 2, y: centerY - halfHeight},
      {x: centerX + halfWidth, y: centerY},
      {x: centerX + halfWidth / 2, y: centerY + halfHeight},
      {x: centerX - halfWidth / 2, y: centerY + halfHeight},
    ],
  };
};

/**
 * Пауза звездочки перед следующей вспышкой
 * @param {function(): number} rng
 * @param {Object} [sparkles]
 * @returns {number} мс
 */
export const resolveSparkleDelayMs = (rng, sparkles = RUINS_INCOME_GLOW.sparkles) => (
  sparkles.delayMinMs + (sparkles.delayMaxMs - sparkles.delayMinMs) * rng()
);

/**
 * Новая вспышка звездочки: где на жетоне (доли его ширины и высоты от
 * центра), какого размера и под каким углом
 * @param {function(): number} rng
 * @param {Object} [sparkles]
 * @returns {{offsetXShare: number, offsetYShare: number, sizeShare: number, angleDeg: number}}
 */
export const createSparkle = (rng, sparkles = RUINS_INCOME_GLOW.sparkles) => {
  const angle = rng() * 2 * Math.PI;
  // Корень — чтобы вспышки ложились по овалу равномерно, а не теснились к центру
  const distance = Math.sqrt(rng());

  return {
    offsetXShare: Math.cos(angle) * distance * sparkles.areaWidthShare,
    offsetYShare: Math.sin(angle) * distance * sparkles.areaHeightShare,
    sizeShare: resolveSpread(rng, sparkles.sizeShare, sparkles.sizeSpread),
    angleDeg: rng() * 90,
  };
};

/**
 * Звездочка в данный момент вспышки: вырастает из точки и гаснет обратно,
 * понемногу поворачиваясь
 * @param {{offsetXShare: number, offsetYShare: number, sizeShare: number, angleDeg: number}} sparkle
 * @param {number} progress — доля вспышки, от 0 до 1
 * @param {{x: number, y: number, width: number, height: number}} token
 * @param {Object} [sparkles]
 * @returns {{x: number, y: number, size: number, angleDeg: number, alpha: number}}
 */
export const resolveSparklePose = (sparkle, progress, {x, y, width, height}, sparkles = RUINS_INCOME_GLOW.sparkles) => {
  const t = clamp01(progress);
  const flash = Math.sin(Math.PI * t);

  return {
    x: x + width * sparkle.offsetXShare,
    y: y + height * sparkle.offsetYShare,
    size: width * sparkle.sizeShare * flash,
    angleDeg: sparkle.angleDeg + sparkles.spinDeg * t,
    alpha: sparkles.alpha * flash,
  };
};
